import React, { useCallback, useEffect, useMemo, useState } from "react"
import axios from "axios"
import { useSearchParams } from "react-router-dom"
import { useActiveClinic } from "../../context/ClinicContext"
import { useClinicPlanSnapshot } from "../../features/subscriptions/hooks/useClinicPlanSnapshot"
import { useClinicSubscription } from "../../features/subscriptions/hooks/useClinicSubscription"
import SettingsModuleScaffold from "./SettingsModuleScaffold"
import { DEFAULT_USER_ID } from "../../config/appConfig"
import {
  resolveClinicLabel,
  humanRoleLabel,
  humanSpecialtyLabel,
  SectionCard,
  FieldBlock,
  MetricCard,
  InsightCard,
  TopStateCard,
  InlineNotice,
  EmptyState,
  EditableListItem,
  EditModalShell,
  inputStyle,
  pillStyle,
  listStyle,
  actionButtonStyle,
  primaryButtonStyle,
  secondaryButtonStyle
} from "./settingsShared"

const tabs = [
  { key: "doctors", label: "Doctores" },
  { key: "users", label: "Usuarios" }
]

const roleOptions = [
  { value: "ADMIN", label: "Administrador" },
  { value: "DOCTOR", label: "Doctor" },
  { value: "RECEPTIONIST", label: "Recepcionista" },
  { value: "ASSISTANT", label: "Asistente" },
  { value: "FINANCE", label: "Financiero" }
]

const specialtyOptions = [
  { value: "", label: "Sin especialidad específica" },
  { value: "GENERAL", label: "General" },
  { value: "ORTODONCIA", label: "Ortodoncia" },
  { value: "ENDODONCIA", label: "Endodoncia" },
  { value: "CIRUGIA", label: "Cirugía" },
  { value: "IMPLANTOLOGIA", label: "Implantología" },
  { value: "PROSTODONCIA", label: "Prostodoncia" },
  { value: "PERIODONCIA", label: "Periodoncia" },
  { value: "PEDIATRIA", label: "Odontopediatría" }
]

export default function TeamAccessPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { activeClinicId } = useActiveClinic()

  const rawTab = searchParams.get("tab")
  const activeTab = rawTab === "users" ? "users" : "doctors"

  const clinicId =
    activeClinicId && activeClinicId !== "ALL" ? activeClinicId : null

  const userId =
    localStorage.getItem("odontoflow_user_id") || DEFAULT_USER_ID

  const [doctors, setDoctors] = useState([])
  const [users, setUsers] = useState([])
  const [clinicRows, setClinicRows] = useState([])

  const [loadingDoctors, setLoadingDoctors] = useState(false)
  const [loadingUsers, setLoadingUsers] = useState(false)

  const [doctorName, setDoctorName] = useState("")
  const [doctorEmail, setDoctorEmail] = useState("")
  const [savingDoctor, setSavingDoctor] = useState(false)

  const [userName, setUserName] = useState("")
  const [userEmail, setUserEmail] = useState("")
  const [userRole, setUserRole] = useState("ASSISTANT")
  const [savingUser, setSavingUser] = useState(false)

  const [editingDoctor, setEditingDoctor] = useState(null)
  const [editDoctorName, setEditDoctorName] = useState("")
  const [editDoctorEmail, setEditDoctorEmail] = useState("")
  const [editDoctorSpecialty, setEditDoctorSpecialty] = useState("")
  const [savingDoctorEdit, setSavingDoctorEdit] = useState(false)

  const [editingUser, setEditingUser] = useState(null)
  const [editUserName, setEditUserName] = useState("")
  const [editUserEmail, setEditUserEmail] = useState("")
  const [editUserRole, setEditUserRole] = useState("ASSISTANT")
  const [savingUserEdit, setSavingUserEdit] = useState(false)

  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  const {
    data: planSnapshot,
    refresh: refreshPlanSnapshot
  } = useClinicPlanSnapshot(clinicId)

  const { subscription } = useClinicSubscription(clinicId)

  const maxDoctors = planSnapshot?.plan?.maxDoctors
  const maxUsers = planSnapshot?.plan?.maxUsers

  const accountName = planSnapshot?.accountName || "tu cuenta"
  const planName = planSnapshot?.plan?.name || "Sin plan activo"

  const isReadOnly = Boolean(subscription?.access?.isReadOnly)
  const readOnlyReason =
    subscription?.access?.reason || "La clínica está en modo solo lectura."

  const doctorsCount =
    doctors.length || Number(planSnapshot?.usage?.doctorsCount || 0)
  const usersCount =
    users.length || Number(planSnapshot?.usage?.usersCount || 0)

  const doctorsLimitReached =
    maxDoctors !== null &&
    maxDoctors !== undefined &&
    doctorsCount >= maxDoctors

  const usersLimitReached =
    maxUsers !== null &&
    maxUsers !== undefined &&
    usersCount >= maxUsers

  const clinicsCount = clinicRows.length
  const activeClinicLabel = useMemo(
    () => resolveClinicLabel(clinicRows, clinicId),
    [clinicRows, clinicId]
  )
  const showActiveClinicMetric = clinicsCount > 1 && !!activeClinicLabel

  const handleTabChange = (tab) => {
    setSearchParams({ tab })
    setError("")
    setSuccess("")
  }

  const loadDoctors = useCallback(async () => {
    if (!clinicId) {
      setDoctors([])
      return
    }

    try {
      setLoadingDoctors(true)
      const { data } = await axios.get("/api/multi-clinic/clinic-doctors", {
        params: { clinicId }
      })
      setDoctors(Array.isArray(data) ? data : [])
    } catch (err) {
      const message =
        err?.response?.data?.message || "No se pudieron cargar los doctores"
      setError(Array.isArray(message) ? message[0] : message)
    } finally {
      setLoadingDoctors(false)
    }
  }, [clinicId])

  const loadUsers = useCallback(async () => {
    if (!clinicId) {
      setUsers([])
      return
    }

    try {
      setLoadingUsers(true)
      const { data } = await axios.get("/api/multi-clinic/clinic-users", {
        params: { clinicId }
      })
      setUsers(Array.isArray(data) ? data : [])
    } catch (err) {
      const message =
        err?.response?.data?.message || "No se pudieron cargar los usuarios"
      setError(Array.isArray(message) ? message[0] : message)
    } finally {
      setLoadingUsers(false)
    }
  }, [clinicId])

  const loadClinics = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/multi-clinic/user-clinics", {
        params: { userId }
      })
      setClinicRows(Array.isArray(data) ? data : [])
    } catch {
      setClinicRows([])
    }
  }, [userId])

  useEffect(() => {
    loadDoctors()
    loadUsers()
    loadClinics()
  }, [loadDoctors, loadUsers, loadClinics])

  async function handleCreateDoctor() {
    try {
      setSavingDoctor(true)
      setError("")
      setSuccess("")

      await axios.post("/api/multi-clinic/add-doctor", {
        clinicId,
        name: doctorName.trim(),
        email: doctorEmail.trim()
      })

      setDoctorName("")
      setDoctorEmail("")

      await loadDoctors()
      await refreshPlanSnapshot?.()

      window.dispatchEvent(new Event("odontoflow:clinics-updated"))
      window.dispatchEvent(new Event("odontoflow:subscription-updated"))

      setSuccess("Doctor creado correctamente.")
    } catch (err) {
      const message =
        err?.response?.data?.message || "No se pudo crear el doctor"
      setError(Array.isArray(message) ? message[0] : message)
    } finally {
      setSavingDoctor(false)
    }
  }

  async function handleCreateUser() {
    try {
      setSavingUser(true)
      setError("")
      setSuccess("")

      await axios.post("/api/multi-clinic/add-user", {
        clinicId,
        name: userName.trim(),
        email: userEmail.trim(),
        role: userRole
      })

      setUserName("")
      setUserEmail("")
      setUserRole("ASSISTANT")

      await loadUsers()
      await refreshPlanSnapshot?.()

      window.dispatchEvent(new Event("odontoflow:clinics-updated"))
      window.dispatchEvent(new Event("odontoflow:subscription-updated"))

      setSuccess("Usuario creado correctamente.")
    } catch (err) {
      const message =
        err?.response?.data?.message || "No se pudo crear el usuario"
      setError(Array.isArray(message) ? message[0] : message)
    } finally {
      setSavingUser(false)
    }
  }

  function openDoctorEditModal(doctor) {
    setEditingDoctor(doctor)
    setEditDoctorName(doctor?.name || "")
    setEditDoctorEmail(doctor?.email || "")
    setEditDoctorSpecialty(doctor?.specialty || "")
    setError("")
    setSuccess("")
  }

  function closeDoctorEditModal() {
    if (savingDoctorEdit) return
    setEditingDoctor(null)
    setEditDoctorName("")
    setEditDoctorEmail("")
    setEditDoctorSpecialty("")
  }

  function openUserEditModal(user) {
    setEditingUser(user)
    setEditUserName(user?.name || "")
    setEditUserEmail(user?.email || "")
    setEditUserRole(user?.role || "ASSISTANT")
    setError("")
    setSuccess("")
  }

  function closeUserEditModal() {
    if (savingUserEdit) return
    setEditingUser(null)
    setEditUserName("")
    setEditUserEmail("")
    setEditUserRole("ASSISTANT")
  }

  async function handleSaveDoctorEdit() {
    try {
      if (!editingDoctor?.id) return

      setSavingDoctorEdit(true)
      setError("")
      setSuccess("")

      await axios.patch("/api/multi-clinic/update-doctor", {
        clinicId,
        doctorId: editingDoctor.id,
        name: editDoctorName.trim(),
        email: editDoctorEmail.trim(),
        specialty: editDoctorSpecialty || null
      })

      await loadDoctors()
      await refreshPlanSnapshot?.()

      closeDoctorEditModal()
      setSuccess("Doctor actualizado correctamente.")
    } catch (err) {
      const message =
        err?.response?.data?.message || "No se pudo actualizar el doctor"
      setError(Array.isArray(message) ? message[0] : message)
    } finally {
      setSavingDoctorEdit(false)
    }
  }

  async function handleSaveUserEdit() {
    try {
      if (!editingUser) return

      setSavingUserEdit(true)
      setError("")
      setSuccess("")

      await axios.patch("/api/multi-clinic/update-user", {
        clinicId,
        membershipId: editingUser.membershipId,
        userId: editingUser.id,
        name: editUserName.trim(),
        email: editUserEmail.trim(),
        role: editUserRole
      })

      await loadUsers()
      await refreshPlanSnapshot?.()

      closeUserEditModal()
      setSuccess("Usuario actualizado correctamente.")
    } catch (err) {
      const message =
        err?.response?.data?.message || "No se pudo actualizar el usuario"
      setError(Array.isArray(message) ? message[0] : message)
    } finally {
      setSavingUserEdit(false)
    }
  }

  const canCreateDoctor =
    !!clinicId &&
    !savingDoctor &&
    !doctorsLimitReached &&
    !isReadOnly &&
    !!doctorName.trim() &&
    !!doctorEmail.trim()

  const canCreateUser =
    !!clinicId &&
    !savingUser &&
    !usersLimitReached &&
    !isReadOnly &&
    !!userName.trim() &&
    !!userEmail.trim()

  const canSaveDoctorEdit =
    !!editingDoctor?.id &&
    !savingDoctorEdit &&
    !!editDoctorName.trim() &&
    !!editDoctorEmail.trim()

  const canSaveUserEdit =
    !!editingUser &&
    !savingUserEdit &&
    !!editUserName.trim() &&
    !!editUserEmail.trim()

  const doctorsStatus = useMemo(() => {
    if (!clinicId) {
      return {
        label: "Selecciona clínica",
        tone: "warning",
        detail: "Debes seleccionar una clínica específica para administrar doctores."
      }
    }

    if (doctorsCount === 0) {
      return {
        label: "Pendiente",
        tone: "warning",
        detail: "Todavía no hay doctores registrados en esta clínica."
      }
    }

    if (doctorsLimitReached) {
      return {
        label: "Límite alcanzado",
        tone: "danger",
        detail: "El plan actual ya alcanzó su capacidad máxima de doctores."
      }
    }

    return {
      label: "Operativo",
      tone: "success",
      detail: "La base médica ya está cargada y todavía tiene margen operativo."
    }
  }, [clinicId, doctorsCount, doctorsLimitReached])

  const usersStatus = useMemo(() => {
    if (!clinicId) {
      return {
        label: "Selecciona clínica",
        tone: "warning",
        detail: "Debes seleccionar una clínica específica para administrar usuarios."
      }
    }

    if (usersCount === 0) {
      return {
        label: "Pendiente",
        tone: "warning",
        detail: "Todavía no hay usuarios operativos registrados en esta clínica."
      }
    }

    if (usersLimitReached) {
      return {
        label: "Límite alcanzado",
        tone: "danger",
        detail: "El plan actual ya alcanzó su capacidad máxima de usuarios."
      }
    }

    return {
      label: "Operativo",
      tone: "success",
      detail: "Los accesos operativos ya están configurados y siguen creciendo dentro del plan."
    }
  }, [clinicId, usersCount, usersLimitReached])

  const actions = (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      <button
        onClick={() => handleTabChange("doctors")}
        style={{
          ...actionButtonStyle,
          background: activeTab === "doctors" ? "#ffffff" : "rgba(255,255,255,0.08)",
          color: activeTab === "doctors" ? "#0f172a" : "#ffffff",
          border:
            activeTab === "doctors"
              ? "1px solid rgba(255,255,255,0.40)"
              : "1px solid rgba(255,255,255,0.16)"
        }}
      >
        Ver doctores
      </button>

      <button
        onClick={() => handleTabChange("users")}
        style={{
          ...actionButtonStyle,
          background: activeTab === "users" ? "#ffffff" : "rgba(255,255,255,0.08)",
          color: activeTab === "users" ? "#0f172a" : "#ffffff",
          border:
            activeTab === "users"
              ? "1px solid rgba(255,255,255,0.40)"
              : "1px solid rgba(255,255,255,0.16)"
        }}
      >
        Ver usuarios
      </button>
    </div>
  )

  return (
    <>
      <SettingsModuleScaffold
        eyebrow="Operación de la clínica"
        title="Equipo y accesos"
        subtitle="Concentra profesionales y usuarios operativos bajo una sola lectura administrativa, sin repartir la gestión del equipo en pantallas separadas."
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        actions={actions}
      >
        {!clinicId ? (
          <TopStateCard tone="warning" title="Clínica no seleccionada">
            Selecciona una clínica específica para administrar doctores y usuarios.
          </TopStateCard>
        ) : null}

        {isReadOnly ? (
          <TopStateCard tone="danger" title="Modo solo lectura">
            {readOnlyReason}
          </TopStateCard>
        ) : null}

        {error ? (
          <TopStateCard tone="danger" title="Error">
            {error}
          </TopStateCard>
        ) : null}

        {success ? (
          <TopStateCard tone="success" title="Listo">
            {success}
          </TopStateCard>
        ) : null}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
            gap: 12
          }}
        >
          <MetricCard
            label="Doctores"
            value={loadingDoctors ? "..." : String(doctorsCount)}
            helper={
              maxDoctors === null || maxDoctors === undefined
                ? "Límite ilimitado"
                : `Límite ${maxDoctors}`
            }
            tone={doctorsStatus.tone}
          />

          <MetricCard
            label="Usuarios"
            value={loadingUsers ? "..." : String(usersCount)}
            helper={
              maxUsers === null || maxUsers === undefined
                ? "Límite ilimitado"
                : `Límite ${maxUsers}`
            }
            tone={usersStatus.tone}
          />

          <MetricCard
            label="Plan"
            value={planName}
            helper={accountName}
            tone="info"
          />

          {showActiveClinicMetric ? (
            <MetricCard
              label="Clínica activa"
              value={activeClinicLabel}
              helper="Contexto actual"
              tone="neutral"
            />
          ) : null}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 12
          }}
        >
          <InsightCard
            title="Doctores y profesionales"
            status={doctorsStatus.label}
            tone={doctorsStatus.tone}
            detail={doctorsStatus.detail}
            ctaLabel="Ir a doctores"
            onClick={() => handleTabChange("doctors")}
          />

          <InsightCard
            title="Usuarios operativos"
            status={usersStatus.label}
            tone={usersStatus.tone}
            detail={usersStatus.detail}
            ctaLabel="Ir a usuarios"
            onClick={() => handleTabChange("users")}
          />
        </div>

        {activeTab === "doctors" ? (
          <div style={{ display: "grid", gap: 14 }}>
            <SectionCard
              title="Alta de doctor"
              subtitle="Crea nuevos profesionales para la clínica activa."
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 12
                }}
              >
                <FieldBlock label="Nombre">
                  <input
                    value={doctorName}
                    onChange={(e) => setDoctorName(e.target.value)}
                    placeholder="Nombre del doctor"
                    style={inputStyle}
                  />
                </FieldBlock>

                <FieldBlock label="Correo">
                  <input
                    value={doctorEmail}
                    onChange={(e) => setDoctorEmail(e.target.value)}
                    placeholder="Correo electrónico"
                    style={inputStyle}
                  />
                </FieldBlock>

                <FieldBlock label="Estado">
                  <div style={pillStyle}>Clínica activa</div>
                </FieldBlock>
              </div>

              <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  onClick={handleCreateDoctor}
                  disabled={!canCreateDoctor}
                  style={{
                    ...primaryButtonStyle,
                    opacity: canCreateDoctor ? 1 : 0.6,
                    cursor: canCreateDoctor ? "pointer" : "not-allowed"
                  }}
                >
                  {savingDoctor ? "Guardando..." : "Crear doctor"}
                </button>
              </div>

              {doctorsLimitReached ? (
                <div style={{ marginTop: 12 }}>
                  <InlineNotice tone="warning">
                    Has alcanzado el límite de doctores de tu plan.
                  </InlineNotice>
                </div>
              ) : null}
            </SectionCard>

            <SectionCard
              title="Listado de doctores"
              subtitle="Equipo clínico registrado en la clínica activa."
            >
              {loadingDoctors ? (
                <EmptyState text="Cargando doctores..." />
              ) : doctors.length === 0 ? (
                <EmptyState text="No hay doctores registrados todavía." />
              ) : (
                <div style={listStyle}>
                  {doctors.map((doctor) => (
                    <EditableListItem
                      key={doctor.id}
                      title={doctor.name || "Doctor"}
                      metaLeft={doctor.email || "Sin correo"}
                      metaRight={
                        doctor.specialty
                          ? humanSpecialtyLabel(doctor.specialty)
                          : "Doctor"
                      }
                      onEdit={() => openDoctorEditModal(doctor)}
                    />
                  ))}
                </div>
              )}
            </SectionCard>
          </div>
        ) : null}

        {activeTab === "users" ? (
          <div style={{ display: "grid", gap: 14 }}>
            <SectionCard
              title="Alta de usuario"
              subtitle="Crea accesos operativos y asígnales un rol principal."
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 12
                }}
              >
                <FieldBlock label="Nombre">
                  <input
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    placeholder="Nombre del usuario"
                    style={inputStyle}
                  />
                </FieldBlock>

                <FieldBlock label="Correo">
                  <input
                    value={userEmail}
                    onChange={(e) => setUserEmail(e.target.value)}
                    placeholder="Correo electrónico"
                    style={inputStyle}
                  />
                </FieldBlock>

                <FieldBlock label="Rol">
                  <select
                    value={userRole}
                    onChange={(e) => setUserRole(e.target.value)}
                    style={inputStyle}
                  >
                    {roleOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </FieldBlock>
              </div>

              <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  onClick={handleCreateUser}
                  disabled={!canCreateUser}
                  style={{
                    ...primaryButtonStyle,
                    opacity: canCreateUser ? 1 : 0.6,
                    cursor: canCreateUser ? "pointer" : "not-allowed"
                  }}
                >
                  {savingUser ? "Guardando..." : "Crear usuario"}
                </button>
              </div>

              {usersLimitReached ? (
                <div style={{ marginTop: 12 }}>
                  <InlineNotice tone="warning">
                    Has alcanzado el límite de usuarios de tu plan.
                  </InlineNotice>
                </div>
              ) : null}
            </SectionCard>

            <SectionCard
              title="Listado de usuarios"
              subtitle="Usuarios operativos registrados en la clínica activa."
            >
              {loadingUsers ? (
                <EmptyState text="Cargando usuarios..." />
              ) : users.length === 0 ? (
                <EmptyState text="No hay usuarios registrados en esta clínica todavía." />
              ) : (
                <div style={listStyle}>
                  {users.map((user) => (
                    <EditableListItem
                      key={user.membershipId || user.id}
                      title={user.name || "Usuario"}
                      metaLeft={user.email || "Sin correo"}
                      metaRight={humanRoleLabel(user.role)}
                      extra={user.specialty || ""}
                      onEdit={() => openUserEditModal(user)}
                    />
                  ))}
                </div>
              )}
            </SectionCard>
          </div>
        ) : null}
      </SettingsModuleScaffold>

      {editingDoctor ? (
        <EditModalShell
          title="Editar doctor"
          subtitle="Ajusta nombre, correo y especialidad principal."
          onClose={closeDoctorEditModal}
          actions={
            <>
              <button
                onClick={closeDoctorEditModal}
                style={secondaryButtonStyle}
                disabled={savingDoctorEdit}
              >
                Cancelar
              </button>

              <button
                onClick={handleSaveDoctorEdit}
                disabled={!canSaveDoctorEdit}
                style={{
                  ...primaryButtonStyle,
                  opacity: canSaveDoctorEdit ? 1 : 0.6,
                  cursor: canSaveDoctorEdit ? "pointer" : "not-allowed"
                }}
              >
                {savingDoctorEdit ? "Guardando..." : "Guardar cambios"}
              </button>
            </>
          }
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 12
            }}
          >
            <FieldBlock label="Nombre">
              <input
                value={editDoctorName}
                onChange={(e) => setEditDoctorName(e.target.value)}
                placeholder="Nombre del doctor"
                style={inputStyle}
              />
            </FieldBlock>

            <FieldBlock label="Correo">
              <input
                value={editDoctorEmail}
                onChange={(e) => setEditDoctorEmail(e.target.value)}
                placeholder="Correo electrónico"
                style={inputStyle}
              />
            </FieldBlock>

            <FieldBlock label="Especialidad principal">
              <select
                value={editDoctorSpecialty}
                onChange={(e) => setEditDoctorSpecialty(e.target.value)}
                style={inputStyle}
              >
                {specialtyOptions.map((option) => (
                  <option key={option.value || "none"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FieldBlock>
          </div>
        </EditModalShell>
      ) : null}

      {editingUser ? (
        <EditModalShell
          title="Editar usuario"
          subtitle="Ajusta nombre, correo y rol principal del usuario."
          onClose={closeUserEditModal}
          actions={
            <>
              <button
                onClick={closeUserEditModal}
                style={secondaryButtonStyle}
                disabled={savingUserEdit}
              >
                Cancelar
              </button>

              <button
                onClick={handleSaveUserEdit}
                disabled={!canSaveUserEdit}
                style={{
                  ...primaryButtonStyle,
                  opacity: canSaveUserEdit ? 1 : 0.6,
                  cursor: canSaveUserEdit ? "pointer" : "not-allowed"
                }}
              >
                {savingUserEdit ? "Guardando..." : "Guardar cambios"}
              </button>
            </>
          }
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 12
            }}
          >
            <FieldBlock label="Nombre">
              <input
                value={editUserName}
                onChange={(e) => setEditUserName(e.target.value)}
                placeholder="Nombre del usuario"
                style={inputStyle}
              />
            </FieldBlock>

            <FieldBlock label="Correo">
              <input
                value={editUserEmail}
                onChange={(e) => setEditUserEmail(e.target.value)}
                placeholder="Correo electrónico"
                style={inputStyle}
              />
            </FieldBlock>

            <FieldBlock label="Rol">
              <select
                value={editUserRole}
                onChange={(e) => setEditUserRole(e.target.value)}
                style={inputStyle}
              >
                {roleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </FieldBlock>
          </div>
        </EditModalShell>
      ) : null}
    </>
  )
}